import { cn } from './cn.js';

/**
 * "Nex" — das Maskottchen und zugleich die Figur des KI-Assistenten (#83–#85).
 *
 * Bewusst ein *Netzwerk-Knoten*: ein Hexagon mit drei Verbindungspunkten. Die Figur ist
 * damit keine beliebige Deko, sondern zeigt, was das Produkt tut — Knoten verbinden.
 *
 * Bewusst SVG statt Bilddatei: skaliert verlustfrei, wiegt nichts, und die Farben kommen
 * aus `currentColor`/den Theme-Tokens — dieselbe Figur funktioniert in Hell und Dunkel.
 */

export type MascotMood =
  | 'idle'      // Standard
  | 'thinking'  // KI arbeitet
  | 'happy'     // Erfolg
  | 'search'    // sucht/findet nichts (Leerzustände)
  | 'sad';      // Fehler

export interface MascotProps {
  mood?: MascotMood;
  size?: number;
  className?: string;
  /** Dezente Idle-/Denk-Animation. Bei `false` bleibt die Figur ruhig. */
  animate?: boolean;
}

export function Mascot({ mood = 'idle', size = 64, className, animate = true }: MascotProps) {
  const thinking = mood === 'thinking';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      role="img"
      aria-label="Nex"
      className={cn(animate && thinking && 'animate-pulse', className)}
    >
      <defs>
        <linearGradient id="nex-body" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--gold-light)" />
          <stop offset="60%" stopColor="var(--gold)" />
          <stop offset="100%" stopColor="var(--gold-dark)" />
        </linearGradient>
      </defs>

      {/* Verbindungslinien zu den Nachbarknoten — das Produktversprechen als Form. */}
      <g stroke="var(--gold)" strokeOpacity="0.45" strokeWidth="1.5" strokeLinecap="round">
        <line x1="32" y1="30" x2="10" y2="14" />
        <line x1="32" y1="30" x2="54" y2="14" />
        <line x1="32" y1="30" x2="32" y2="57" />
      </g>
      <g fill="var(--gold)">
        <circle cx="10" cy="14" r={mood === 'happy' ? 4 : 3} opacity="0.9" />
        <circle cx="54" cy="14" r={mood === 'happy' ? 4 : 3} opacity="0.9" />
        <circle cx="32" cy="57" r={mood === 'happy' ? 4 : 3} opacity="0.9" />
      </g>

      {/* Körper: Hexagon = Knoten */}
      <path
        d="M32 10 L50 20 L50 40 L32 50 L14 40 L14 20 Z"
        fill="url(#nex-body)"
        stroke="var(--gold-dark)"
        strokeWidth="1"
        strokeLinejoin="round"
      />

      <Face mood={mood} />
    </svg>
  );
}

function Face({ mood }: { mood: MascotMood }) {
  // Augenfarbe bewusst aus dem dunklen Ink-Ton: sitzt in beiden Themes auf dem Gold-Körper.
  const eye = 'var(--ink)';

  if (mood === 'thinking') {
    return (
      <g fill={eye}>
        {/* Zusammengekniffene Augen + Denkpunkte */}
        <rect x="23" y="27" width="6" height="2.5" rx="1.25" />
        <rect x="35" y="27" width="6" height="2.5" rx="1.25" />
        <g opacity="0.75">
          <circle cx="27" cy="37" r="1.6" />
          <circle cx="32" cy="37" r="1.6" />
          <circle cx="37" cy="37" r="1.6" />
        </g>
      </g>
    );
  }

  if (mood === 'happy') {
    return (
      <g fill={eye}>
        {/* Lachende Augenbögen */}
        <path
          d="M23 28 Q26 24.5 29 28"
          stroke={eye}
          strokeWidth="2.4"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M35 28 Q38 24.5 41 28"
          stroke={eye}
          strokeWidth="2.4"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M26 35 Q32 41 38 35"
          stroke={eye}
          strokeWidth="2.4"
          strokeLinecap="round"
          fill="none"
        />
      </g>
    );
  }

  if (mood === 'sad') {
    return (
      <g>
        <circle cx="26" cy="28" r="2.6" fill={eye} />
        <circle cx="38" cy="28" r="2.6" fill={eye} />
        <path
          d="M26 39 Q32 34 38 39"
          stroke={eye}
          strokeWidth="2.4"
          strokeLinecap="round"
          fill="none"
        />
      </g>
    );
  }

  if (mood === 'search') {
    return (
      <g>
        {/* Ein Auge als Lupe — sucht. */}
        <circle cx="26" cy="28" r="2.6" fill={eye} />
        <circle
          cx="38"
          cy="28"
          r="4.5"
          stroke={eye}
          strokeWidth="1.8"
          fill="none"
          opacity="0.9"
        />
        <line
          x1="41"
          y1="31.5"
          x2="44"
          y2="34.5"
          stroke={eye}
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M27 38 Q32 36.5 37 38"
          stroke={eye}
          strokeWidth="2.2"
          strokeLinecap="round"
          fill="none"
        />
      </g>
    );
  }

  return (
    <g>
      <circle cx="26" cy="28" r="2.8" fill={eye} />
      <circle cx="38" cy="28" r="2.8" fill={eye} />
      <path
        d="M26 36 Q32 40 38 36"
        stroke={eye}
        strokeWidth="2.4"
        strokeLinecap="round"
        fill="none"
      />
    </g>
  );
}
